import graphql__caa0cb42851e0348910c from "./__generated__/VisualBlockEditorPreviewQuery.graphql.ts";import * as React from "react";
import {useCallback, useEffect, useRef, useState} from "react";
import {useIntl} from "react-intl";
import {graphql, fetchQuery} from "react-relay";
import {useRelayEnvironment} from "react-relay/hooks";
import BlockPalette from "./BlockPalette";
import BlockEditorPanel from "./BlockEditorPanel";
import BlockOverlayControls from "./BlockOverlayControls";
import {
    EmailBlock,
    BlockType,
    BLOCK_TYPES,
    generateBlockId,
    parseBlocks,
    stringifyBlocks,
} from "../../types/emailBlocks";

// Query to get the preview HTML
const previewQuery = graphql__caa0cb42851e0348910c;

interface BlockPosition {
    blockId: string;
    blockType: string;
    top: number;
    left: number;
    width: number;
    height: number;
}

interface EmailTemplateImage {
    id: string;
    emailTemplateImageIdentifier: string;
    url: string;
    originalFilename: string;
    width: number;
    height: number;
    mediaType: string;
    fileSize: number;
}

interface VisualBlockEditorProps {
    value: string;
    onChange: (value: string) => void;
    emailTemplateId: string;
    language: string;
    images: EmailTemplateImage[];
}

const VisualBlockEditor: React.FC<VisualBlockEditorProps> = ({
    value,
    onChange,
    emailTemplateId,
    language,
    images,
}) => {
    const intl = useIntl();
    const environment = useRelayEnvironment();
    const iframeRef = useRef<HTMLIFrameElement>(null);
    const previewContainerRef = useRef<HTMLDivElement>(null);

    const [previewHtml, setPreviewHtml] = useState<string>("");
    const [isLoading, setIsLoading] = useState(true);
    const [blockPositions, setBlockPositions] = useState<BlockPosition[]>([]);
    const [hoveredBlockId, setHoveredBlockId] = useState<string | null>(null);
    const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
    const [iframeOffset, setIframeOffset] = useState({top: 0, left: 0});
    const [iframeHeight, setIframeHeight] = useState(600);

    // Parse blocks from JSON string
    const blocks = React.useMemo(() => parseBlocks(value), [value]);

    // Update blocks and stringify back to JSON
    const updateBlocks = useCallback(
        (newBlocks: EmailBlock[]) => {
            onChange(stringifyBlocks(newBlocks));
        },
        [onChange]
    );

    // Track previous block count to detect deletions
    const prevBlockCountRef = React.useRef<number>(blocks.length);
    useEffect(() => {
        // If blocks were deleted, reset height to allow shrinking
        if (blocks.length < prevBlockCountRef.current) {
            setIframeHeight(400);
        }
        prevBlockCountRef.current = blocks.length;
    }, [blocks.length]);

    // Fetch preview HTML with debouncing
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setIsLoading(true);
            fetchQuery(environment, previewQuery, {
                emailTemplateId,
                language,
                htmlContent: value || "[]",
                textContent: "",
            }).subscribe({
                next: (data: any) => {
                    if (data?.emailTemplatePreview?.htmlBody) {
                        setPreviewHtml(data.emailTemplatePreview.htmlBody);
                    }
                    setIsLoading(false);
                },
                error: () => {
                    setIsLoading(false);
                },
            });
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [environment, emailTemplateId, language, value]);

    // Inject script into iframe to detect block positions
    useEffect(() => {
        if (!previewHtml || !iframeRef.current) return;

        const iframe = iframeRef.current;
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc) return;

        // Write the preview HTML to iframe
        iframeDoc.open();
        iframeDoc.write(previewHtml);
        iframeDoc.close();

        // Wait for content to render, then find block positions and adjust height
        const detectBlocks = () => {
            const blockElements = iframeDoc.querySelectorAll("[data-block-id]");
            const positions: BlockPosition[] = [];

            blockElements.forEach((block) => {
                const rect = block.getBoundingClientRect();
                const blockId = block.getAttribute("data-block-id") || "";
                const blockType = block.getAttribute("data-block-type") || "";

                positions.push({
                    blockId,
                    blockType,
                    top: rect.top,
                    left: rect.left,
                    width: rect.width,
                    height: rect.height,
                });
            });

            setBlockPositions(positions);

            // Adjust iframe height to fit content
            const body = iframeDoc.body;
            const html = iframeDoc.documentElement;
            if (body && html) {
                const contentHeight = Math.max(
                    body.scrollHeight,
                    body.offsetHeight,
                    html.clientHeight,
                    html.scrollHeight,
                    html.offsetHeight
                );
                // Add some padding and set minimum height
                setIframeHeight(Math.max(contentHeight + 20, 400));
            }
        };

        // Detect blocks after a short delay to ensure rendering is complete
        const timerId = setTimeout(detectBlocks, 100);

        // Also detect on scroll
        const handleScroll = () => detectBlocks();
        iframeDoc.addEventListener("scroll", handleScroll);

        return () => {
            clearTimeout(timerId);
            iframeDoc.removeEventListener("scroll", handleScroll);
        };
    }, [previewHtml]);

    // Track iframe position relative to preview container
    useEffect(() => {
        const updateIframeOffset = () => {
            if (iframeRef.current && previewContainerRef.current) {
                const iframeRect = iframeRef.current.getBoundingClientRect();
                const containerRect = previewContainerRef.current.getBoundingClientRect();
                setIframeOffset({
                    top: iframeRect.top - containerRect.top,
                    left: iframeRect.left - containerRect.left,
                });
            }
        };

        // Small delay to ensure layout is complete
        const timerId = setTimeout(updateIframeOffset, 50);
        window.addEventListener("resize", updateIframeOffset);
        window.addEventListener("scroll", updateIframeOffset);

        return () => {
            clearTimeout(timerId);
            window.removeEventListener("resize", updateIframeOffset);
            window.removeEventListener("scroll", updateIframeOffset);
        };
    }, [iframeHeight, previewHtml]);

    // Handle block content update from editor panel
    const handleBlockContentChange = useCallback(
        (content: string) => {
            if (!selectedBlockId) return;
            const newBlocks = blocks.map((block) =>
                block.id === selectedBlockId ? {...block, content} : block
            );
            updateBlocks(newBlocks);
        },
        [blocks, selectedBlockId, updateBlocks]
    );

    // Handle block update with partial fields (for image blocks)
    const handleBlockUpdate = useCallback(
        (updates: Partial<EmailBlock>) => {
            if (!selectedBlockId) return;
            const newBlocks = blocks.map((block) =>
                block.id === selectedBlockId ? {...block, ...updates} : block
            );
            updateBlocks(newBlocks);
        },
        [blocks, selectedBlockId, updateBlocks]
    );

    // Handle block move up
    const handleMoveUp = useCallback(
        (blockId: string) => {
            const index = blocks.findIndex((b) => b.id === blockId);
            if (index <= 0) return;
            const newBlocks = [...blocks];
            [newBlocks[index - 1], newBlocks[index]] = [
                newBlocks[index],
                newBlocks[index - 1],
            ];
            updateBlocks(newBlocks);
        },
        [blocks, updateBlocks]
    );

    // Handle block move down
    const handleMoveDown = useCallback(
        (blockId: string) => {
            const index = blocks.findIndex((b) => b.id === blockId);
            if (index < 0 || index >= blocks.length - 1) return;
            const newBlocks = [...blocks];
            [newBlocks[index], newBlocks[index + 1]] = [
                newBlocks[index + 1],
                newBlocks[index],
            ];
            updateBlocks(newBlocks);
        },
        [blocks, updateBlocks]
    );

    // Handle block delete
    const handleDelete = useCallback(
        (blockId: string) => {
            const newBlocks = blocks.filter((b) => b.id !== blockId);
            updateBlocks(newBlocks);
            if (selectedBlockId === blockId) {
                setSelectedBlockId(null);
            }
        },
        [blocks, selectedBlockId, updateBlocks]
    );

    // Handle add block from palette - auto-select the new block
    const handleAddBlock = useCallback(
        (type: BlockType) => {
            const blockConfig = BLOCK_TYPES.find((bt) => bt.type === type);
            const newBlock: EmailBlock = {
                id: generateBlockId(),
                type,
                content: blockConfig?.defaultContent || "",
            };
            updateBlocks([...blocks, newBlock]);
            setSelectedBlockId(newBlock.id);
        },
        [blocks, updateBlocks]
    );

    // Get block index for controls
    const getBlockIndex = (blockId: string): number => {
        return blocks.findIndex((b) => b.id === blockId);
    };

    // Get selected block for editor panel
    const selectedBlock = selectedBlockId
        ? blocks.find((b) => b.id === selectedBlockId) || null
        : null;

    return (
        <div className="visual-block-editor flex border-t border-gray-200" style={{minHeight: Math.max(iframeHeight, 400)}}>
            {/* Left Side: Palette + Editor */}
            <div className="w-1/2 flex flex-col border-r border-gray-200 bg-gray-50">
                {/* Block Palette */}
                <BlockPalette onAddBlock={handleAddBlock} />

                {/* Editor Panel */}
                <BlockEditorPanel
                    block={selectedBlock}
                    emailTemplateId={emailTemplateId}
                    images={images}
                    onContentChange={handleBlockContentChange}
                    onBlockChange={handleBlockUpdate}
                    onClose={() => setSelectedBlockId(null)}
                />
            </div>

            {/* Right Side: Preview */}
            <div className="w-1/2 relative bg-gray-100" ref={previewContainerRef}>
                {/* Loading indicator */}
                {isLoading && (
                    <div className="absolute top-2 right-2 z-10">
                        <span className="text-xs text-gray-400 bg-white/80 px-2 py-1 rounded-sm">
                            {intl.formatMessage({id: "common.loading"})}
                        </span>
                    </div>
                )}

                {/* Preview iframe */}
                <iframe
                    ref={iframeRef}
                    className="w-full border-0 bg-white"
                    style={{height: iframeHeight}}
                    title="Email Preview"
                />

                {/* Block Overlay Controls */}
                {blockPositions.map((pos) => {
                    const blockIndex = getBlockIndex(pos.blockId);
                    const isHovered = hoveredBlockId === pos.blockId;
                    const isSelected = selectedBlockId === pos.blockId;

                    return (
                        <BlockOverlayControls
                            key={pos.blockId}
                            blockId={pos.blockId}
                            blockType={pos.blockType}
                            position={{
                                top: pos.top + iframeOffset.top,
                                left: pos.left + iframeOffset.left,
                                width: pos.width,
                                height: pos.height,
                            }}
                            isFirst={blockIndex === 0}
                            isLast={blockIndex === blocks.length - 1}
                            isHovered={isHovered}
                            isSelected={isSelected}
                            onHover={() => setHoveredBlockId(pos.blockId)}
                            onLeave={() => setHoveredBlockId(null)}
                            onSelect={() => setSelectedBlockId(pos.blockId)}
                            onMoveUp={() => handleMoveUp(pos.blockId)}
                            onMoveDown={() => handleMoveDown(pos.blockId)}
                            onDelete={() => handleDelete(pos.blockId)}
                        />
                    );
                })}

                {/* Empty state for preview */}
                {blocks.length === 0 && !isLoading && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className="bg-white/90 rounded-lg p-6 text-center">
                            <p className="text-gray-500 text-sm">
                                {intl.formatMessage({id: "emailBlock.empty"})}
                            </p>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default VisualBlockEditor;
